import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildingTypes,
  propertyUpdateSchema,
  type Property,
  type PropertyUpdate,
} from '@vuokramani/shared';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogSubhead,
  SelectField,
  TextField,
} from '../../components/ui.tsx';
import { pageStyles as s } from '../../components/page.tsx';
import { errorKey } from '../../lib/api.ts';
import { toNumber, useUpdateProperty } from '../../lib/properties.ts';

type Errors = Record<string, string>;
type Values = Record<string, string>;

const str = (v: string | number | null | undefined) =>
  v === null || v === undefined ? '' : String(v);

/** Yhteinen lomakelogiikka: arvot merkkijonoina, tarkistus jaetulla skeemalla, tallennus PATCHilla. */
function useEditForm(
  property: Property,
  initial: Values,
  toPayload: (v: Values) => PropertyUpdate,
  onDone: () => void,
) {
  const [values, setValues] = useState<Values>(initial);
  const [errors, setErrors] = useState<Errors>({});
  const update = useUpdateProperty(property.id);

  const field = (key: string) => ({
    value: values[key] ?? '',
    onChange: (e: { target: { value: string } }) =>
      setValues((v) => ({ ...v, [key]: e.target.value })),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    const parsed = propertyUpdateSchema.safeParse(toPayload(values));
    if (!parsed.success) {
      const next: Errors = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0]);
        next[key] ??= issue.message.startsWith('errors.') ? issue.message : 'errors.number';
      }
      setErrors(next);
      return;
    }
    setErrors({});
    update.mutate(parsed.data, { onSuccess: onDone });
  }

  return { values, errors, field, submit, update };
}

interface EditDialogProps {
  property: Property;
  open: boolean;
  onClose: () => void;
}

/** Asunnon tiedot: osoite, tyyppi, koko, kerros, rakennusvuosi, oma nimi ja omistusosuus. */
export function EditHomeDialog({ property: p, open, onClose }: EditDialogProps) {
  const { t } = useTranslation();
  const { errors, field, submit, update } = useEditForm(
    p,
    {
      buildingType: str(p.buildingType),
      streetAddress: p.streetAddress,
      postalCode: p.postalCode,
      city: p.city,
      roomLayout: str(p.roomLayout),
      areaM2: str(p.areaM2).replace('.', ','),
      floor: str(p.floor),
      buildYear: str(p.buildYear),
      name: str(p.name),
      ownershipPercent:
        p.myOwnershipBp !== null ? String(p.myOwnershipBp / 100).replace('.', ',') : '',
    },
    (v) => ({
      buildingType: (v.buildingType || null) as PropertyUpdate['buildingType'],
      streetAddress: v.streetAddress,
      postalCode: v.postalCode,
      city: v.city,
      roomLayout: v.roomLayout,
      areaM2: toNumber(v.areaM2 ?? ''),
      floor: toNumber(v.floor ?? ''),
      buildYear: toNumber(v.buildYear ?? ''),
      name: v.name,
      ownershipPercent: toNumber(v.ownershipPercent ?? '') ?? undefined,
    }),
    onClose,
  );
  const err = (k: string) => (errors[k] ? t(errors[k]) : undefined);

  return (
    <Dialog open={open} onClose={onClose} title={t('edit.homeTitle')} wide>
      <form className={s.form} onSubmit={submit} noValidate>
        <div className={s.formRow}>
          <SelectField label={t('property.fields.type')} value={p.type} disabled>
            <option value={p.type}>{t(`propertyTypes.${p.type}`)}</option>
          </SelectField>
          <SelectField label={t('property.fields.buildingType')} {...field('buildingType')}>
            <option value="">{t('common.notSet')}</option>
            {buildingTypes.map((b) => (
              <option key={b} value={b}>
                {t(`buildingTypes.${b}`)}
              </option>
            ))}
          </SelectField>
        </div>
        <TextField
          label={t('newProperty.streetAddress')}
          {...field('streetAddress')}
          error={err('streetAddress')}
        />
        <div className={s.formRowNarrow}>
          <TextField
            label={t('newProperty.postalCode')}
            inputMode="numeric"
            maxLength={5}
            {...field('postalCode')}
            error={err('postalCode')}
          />
          <TextField label={t('newProperty.city')} {...field('city')} error={err('city')} />
        </div>
        <div className={s.formRow}>
          <TextField
            label={t('newProperty.roomLayout')}
            hint={t('newProperty.roomLayoutHint')}
            {...field('roomLayout')}
          />
          <TextField
            label={t('newProperty.areaM2')}
            inputMode="decimal"
            {...field('areaM2')}
            error={err('areaM2')}
          />
        </div>
        <div className={s.formRow}>
          <TextField
            label={t('property.fields.floor')}
            inputMode="numeric"
            {...field('floor')}
            error={err('floor')}
          />
          <TextField
            label={t('property.fields.buildYear')}
            inputMode="numeric"
            maxLength={4}
            {...field('buildYear')}
            error={err('buildYear')}
          />
        </div>
        <DialogSubhead>{t('edit.ownOnly')}</DialogSubhead>
        <div className={s.formRow}>
          <TextField
            label={t('newProperty.name')}
            hint={t('newProperty.nameHint')}
            {...field('name')}
          />
          <TextField
            label={t('newProperty.ownershipPercent')}
            hint={t('edit.ownershipHint')}
            inputMode="decimal"
            {...field('ownershipPercent')}
            error={err('ownershipPercent')}
          />
        </div>
        {update.isError && <Alert>{t(errorKey(update.error))}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={update.isPending}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** Taloyhtiö: nimi ja Y-tunnus. Yhteystiedot lisätään listaan erikseen. */
export function EditCompanyDialog({ property: p, open, onClose }: EditDialogProps) {
  const { t } = useTranslation();
  const keys = ['housingCompany', 'housingCompanyBusinessId'] as const;
  const { errors, field, submit, update } = useEditForm(
    p,
    Object.fromEntries(keys.map((k) => [k, str(p[k])])),
    (v) => Object.fromEntries(keys.map((k) => [k, v[k] ?? ''])) as PropertyUpdate,
    onClose,
  );
  const err = (k: string) => (errors[k] ? t(errors[k]) : undefined);

  return (
    <Dialog open={open} onClose={onClose} title={t('edit.companyTitle')}>
      <form className={s.form} onSubmit={submit} noValidate>
        <TextField label={t('newProperty.housingCompany')} {...field('housingCompany')} />
        <TextField
          label={t('newProperty.businessId')}
          placeholder="1234567-8"
          {...field('housingCompanyBusinessId')}
          error={err('housingCompanyBusinessId')}
        />
        {update.isError && <Alert>{t(errorKey(update.error))}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={update.isPending}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
